import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { BoxAppearance } from '@lub0v/santa-utils';
import { santaClient } from './client';
import { useAppSelector } from './hooks';
import { useTheme } from '@src/theme/useTheme';
import { loadSeason } from '@src/theme/registry';
import { useTranslation } from 'react-i18next';
import './BoxAppearance.css';
import { useLocation } from 'react-router-dom';
import { currentLocalSeasonPreview } from '@src/theme/localSeasonPreview';
import { DEFAULT_GAME_SEASON, isSeason } from '@src/theme/season';

const Context = createContext<{ appearance: BoxAppearance | null; loading: boolean }>({
  appearance: null,
  loading: true,
});
/** UI visibility only. Backend endpoints must independently enforce Premium access. */
export function useBoxFeatures() {
  const { appearance, loading } = useContext(Context);
  return { isPremium: appearance?.isPremium === true, theme: appearance?.theme ?? null, loading };
}
export function BoxAppearanceProvider({
  boxKey,
  children,
}: {
  boxKey: string;
  children: ReactNode;
}) {
  const [result, setResult] = useState<{ key: string; data: BoxAppearance | null } | null>(null);
  const info = useAppSelector((state) => state.box.info);
  const { setSeasonOverride, season } = useTheme();
  const { t } = useTranslation();
  const [failedTheme, setFailedTheme] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    void santaClient
      .getBoxAppearance({ key: boxKey })
      .then((res) => {
        if (active) setResult({ key: boxKey, data: res.ok && res.data ? res.data : null });
      })
      .catch(() => {
        if (active) setResult({ key: boxKey, data: null });
      });
    return () => {
      active = false;
    };
  }, [boxKey]);
  const appearance =
    info?.box.key === boxKey
      ? { key: boxKey, theme: info.box.theme ?? null, isPremium: info.box.isPremium }
      : result?.key === boxKey
        ? result.data
        : null;
  const { search } = useLocation();
  const theme = currentLocalSeasonPreview(search) ?? appearance?.theme ?? DEFAULT_GAME_SEASON;
  useEffect(() => {
    let active = true;
    setSeasonOverride(isSeason(theme) ? theme : DEFAULT_GAME_SEASON);
    if (isSeason(theme)) {
      void loadSeason(theme).catch(() => {
        if (active) setFailedTheme(theme);
      });
    }
    return () => {
      active = false;
      setSeasonOverride(null);
    };
  }, [theme, setSeasonOverride]);
  const loading =
    (!appearance && result?.key !== boxKey) ||
    (isSeason(theme) && season !== theme && failedTheme !== theme);
  return (
    <Context.Provider value={{ appearance, loading }}>
      <div
        className={`box-appearance-loading${loading ? '' : ' box-appearance-loading--ready'}`}
        role={loading ? 'status' : undefined}
        aria-hidden={!loading || undefined}
      >
        {loading ? t('box.loading') : null}
      </div>
      <div className="box-appearance-content" hidden={loading} aria-hidden={loading || undefined}>
        {children}
      </div>
    </Context.Provider>
  );
}
